import { useEffect, useState } from "react";
import _ from "lodash";

import {
  getDocumentArchiveOptions,
  isGrandparentNode
} from "views/documents/state/documentArchiveOptions";
import { ArchiveNodeOption } from "models/ArchiveNodeOption";
import UserFpTreeItem from "./UserFpTreeItem";
import UserServiceTreeItem from "./UserServiceTreeItem";
import UserServiceView from "./UserServiceView";
import { useRootStore } from "store";

import "./UserServiceTree.scss";

type Props = {
  fpConfig: any;
  onSetFpService: (customerChoice: string, userId: string) => void;
  userId: string;
  isInternal: boolean;
  onToggleService: (userId: string, serviceId: string, value: boolean) => void;
  onToggleChildServices: (
    userId: string,
    serviceIds: string[],
    value: boolean
  ) => void;
  disableFp?: boolean;
};

export default function UserServiceTree({
  fpConfig,
  onSetFpService,
  userId,
  isInternal,
  onToggleService,
  onToggleChildServices,
  disableFp
}: Props) {
  const { documentServices } = useRootStore((state) => state.wizard.customer);

  const renderView = (flatServices: any[]) => {
    const documentNodes = _.cloneDeep(
      getDocumentArchiveOptions(documentServices)
    );
    renderTreeBranch(documentNodes, flatServices);
    renderCheckboxView(documentNodes, flatServices);
    return { documentNodes };
  };

  const renderCheckboxView = (
    service: ArchiveNodeOption,
    flatServices: any[]
  ) => {
    service.nodes = service.nodes.map((node) => {
      const items = flatServices.filter((fs) => fs.id === node.id);

      if (node.nodes.length > 0) {
        renderCheckboxView(node, flatServices);

        // if all children are active, set parent active
        node.active =
          node.nodes.filter((n) => n.active).length === node.nodes.length;
      } else if (items.length > 0) {
        node.active =
          items[0].userIds.filter((id: string) => id === userId).length > 0;
      }

      if (userId !== undefined && userId.startsWith("InternalUsers")) {
        node.locked = true;
      } else {
        node.locked = false;
      }

      return node;
    });
  };

  const renderTreeBranch = (
    service: ArchiveNodeOption,
    flatServices: any[]
  ) => {
    service.nodes = service.nodes
      .map((node) => {
        const items = flatServices.filter((fs) => fs.id === node.id);

        if (node.nodes.length > 0) {
          renderTreeBranch(node, flatServices);
        }

        if ((items.length > 0 || node.enabled) && !node.hidden) {
          service.enabled = true;
          node.enabled = true;
          return node;
        }
        return undefined;
      })
      .filter((node): node is ArchiveNodeOption => node !== undefined);
  };

  const [documentNodes, setDocumentNodes] = useState(
    renderView(documentServices || []).documentNodes
  );

  const [nodeClicked, setNodeClicked] = useState(
    renderView(documentServices || []).documentNodes
  );

  const handleSetNodeClicked = (
    service: ArchiveNodeOption,
    serviceId: string
  ) => {
    if (service.id === serviceId) {
      setNodeClicked(service);
    } else if (service.nodes.length > 0) {
      service.nodes.forEach((s) => {
        handleSetNodeClicked(s, serviceId);
      });
    }
  };

  useEffect(() => {
    const updatedDocs = renderView(documentServices || []);
    handleSetNodeClicked(updatedDocs.documentNodes, nodeClicked.id);
    setDocumentNodes(updatedDocs.documentNodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentServices, nodeClicked.id]);

  const findAndSetSelectedNode = (
    allNodes: ArchiveNodeOption,
    serviceId: string
  ) => {
    const firstLevelNodes = allNodes.nodes;
    for (const node of firstLevelNodes) {
      if (node.id === serviceId) {
        setNodeClicked(allNodes);
        return true;
      }
      if (node.nodes && node.nodes.length > 0) {
        if (findAndSetSelectedNode(node, serviceId)) {
          setNodeClicked(node);
          return true;
        }
      }
    }
    return false;
  };

  const handleNodeClick = (
    service: ArchiveNodeOption,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.preventDefault();
    const shouldClose = isGrandparentNode(service);
    const isSameNode = nodeClicked.id === service.id && shouldClose;
    const isNodeExpanded = nodeClicked.id.startsWith(service.id) && shouldClose;

    if (isSameNode || isNodeExpanded) {
      findAndSetSelectedNode(documentNodes, service.id);
    } else {
      setNodeClicked(service);
    }
  };

  const setService = (service: ArchiveNodeOption, value: boolean) => {
    const childless = service.nodes.length === 0;
    if (childless) {
      onToggleService(userId, service.id, value);
    } else {
      const serviceIds: string[] = _.flattenDeep(getAllLeafs(service));
      onToggleChildServices(userId, serviceIds, value);
    }
  };

  const getAllLeafs = (service: any) => {
    return service.nodes.map((serv: any) => {
      return serv.nodes.length === 0 ? serv.id : getAllLeafs(serv);
    });
  };

  const handleSetFpService = (customerChoice: string) => {
    onSetFpService(customerChoice, userId);
  };

  const unique = userId + "_" + nodeClicked.id;

  return (
    <div className="content">
      <ul className="userServiceWrapper">
        <UserServiceTreeItem
          service={documentNodes}
          onNodeClick={handleNodeClick}
          selectedNode={nodeClicked}
        />
        {!disableFp && (
          <UserFpTreeItem
            onNodeClick={fpConfig ? handleNodeClick : undefined}
            selectedNode={nodeClicked}
          />
        )}
      </ul>
      <UserServiceView
        userId={userId}
        fpConfig={fpConfig}
        unique={unique}
        isInternal={isInternal}
        parent={nodeClicked}
        onSetService={setService}
        onSetFpService={handleSetFpService}
      />
    </div>
  );
}
